/**
 * Report-reference extraction for the dashboard paste-box lookup
 * (TRIAG-252).
 *
 * Operators triaging a client report from Slack paste free-form text
 * — typically the empire-admin client-report link plus the JSON
 * report blob — and the lookup modal needs to pull out the report
 * UUID so it can resolve the bundle.
 *
 * Sibling of `lib/paste-detect.ts` (TRIAG-143), which does the same
 * trick for Jira keys. Kept as a pure `.ts` module (no React) so the
 * regex is dead-code-eliminable, unit-testable in isolation, and
 * reusable by the future `@triage` Slack lookup (which shares this
 * extraction core).
 *
 * # Why full-UUID-only
 *
 * We deliberately match ONLY the canonical 36-char 8-4-4-4-12 form,
 * never a short prefix. A short prefix can collide across bundles and
 * the resolver would have to guess (the silent-first-match footgun
 * the UUID-standardization work hardens). A full UUID is unambiguous,
 * so the modal resolves it by exact `analysis_id` match with zero
 * guessing. Short-prefix support waits for the hardened resolver.
 *
 * The empire-admin URL (`.../client-reports/<uuid>`), our own
 * dashboard URLs (`/bundles|/analyses|/duplicates/<uuid>`), and a
 * bare UUID all carry the UUID literally, so a single first-match
 * scan handles every paste shape with one regex. Non-UUID hex tokens
 * in the same blob — Mongo ObjectIds like `667990e19...` (24
 * contiguous hex, no dashes), build numbers, etc. — do NOT match the
 * dashed 8-4-4-4-12 form, so they can't be mistaken for the report id.
 */

/**
 * Canonical UUID: 8-4-4-4-12 hex. Case-insensitive + global so we can
 * scan for EVERY occurrence anywhere in the pasted text. The dashed
 * shape is specific enough that a substring scan won't false-positive
 * on contiguous-hex tokens.
 *
 * `matchAll` clones the regex per call, so reusing this module-level
 * global pattern across calls is safe (no shared `lastIndex` state).
 */
const UUID_PATTERN =
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

/**
 * Cap on how many DISTINCT UUIDs we surface from a single paste. A
 * forwarded thread could contain many links; the pick-list only needs
 * to offer the operator a handful, and each candidate triggers a
 * resolve call — so we bound the work a pathological paste can cause.
 */
const DEFAULT_MAX_UUIDS = 5;

/**
 * Extract the DISTINCT full report UUIDs from pasted text, normalized
 * to lower-case, in first-seen order, capped at `max`.
 *
 * Deduping is the load-bearing behavior: a single report is often
 * linked several ways in one paste (the empire-admin URL + a dashboard
 * URL + the raw UUID in the JSON), all carrying the SAME id — those
 * collapse to one entry, so the modal only prompts the operator to
 * choose when the paste genuinely references more than one report.
 *
 * Returns `[]` when no full UUID is present (short prefixes, unrelated
 * text) — the caller surfaces a "couldn't find a full report UUID"
 * message rather than guessing.
 */
export function extractReportUuids(
  pasted: string,
  max: number = DEFAULT_MAX_UUIDS,
): string[] {
  const seen = new Set<string>();
  for (const match of pasted.matchAll(UUID_PATTERN)) {
    seen.add(match[0].toLowerCase());
    if (seen.size >= max) break;
  }
  return [...seen];
}

/**
 * Convenience: the first distinct UUID (or `null`). Thin wrapper over
 * [`extractReportUuids`] for the single-id callers + tests.
 */
export function extractReportUuid(pasted: string): string | null {
  return extractReportUuids(pasted, 1)[0] ?? null;
}
